import React, { useEffect, useState } from "react";
import { CheckCircle2, Camera, X } from "lucide-react";
import { fetchMedLibrary } from "@/lib/api";

/* Downscale to a small JPEG data-URL so photos fit in localStorage (~40KB each). */
const compressPhoto = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      const MAX = 420;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.7));
    };
    img.onerror = reject;
    img.src = url;
  });

const EMPTY = {
  generic_name: "",
  brand_name: "",
  strength: "",
  directions: "",
  purpose: "",
  times: [],
  form: "tablet",
  imprint_front: "",
  imprint_back: "",
  manufacturer: "",
  pharmacy: "",
  do_not_confuse: "",
  photo: "",
  label_reviewed: false,
};

const TIMES = [
  { key: "morning", label: "Morning" },
  { key: "noon", label: "Noon" },
  { key: "evening", label: "Evening" },
  { key: "bedtime", label: "Bedtime" },
];

const FORMS = ["tablet", "capsule", "softgel", "liquid", "patch", "inhaler", "injection", "cream", "other"];

export default function MedicationForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [suggestions, setSuggestions] = useState([]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setCheck = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.checked }));

  const toggleTime = (t) => {
    setF((p) => ({
      ...p,
      times: p.times.includes(t) ? p.times.filter((x) => x !== t) : [...p.times, t],
    }));
  };

  // Simple autocomplete against the curated library
  useEffect(() => {
    const q = f.generic_name.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      fetchMedLibrary({ q })
        .then((r) => {
          if (!cancelled) setSuggestions((r.items || []).slice(0, 5));
        })
        .catch(() => setSuggestions([]));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [f.generic_name]);

  const applySuggestion = (s) => {
    setF((p) => ({
      ...p,
      generic_name: s.display,
      brand_name: p.brand_name || (s.brands || [])[0] || "",
      purpose: p.purpose || s.purpose || "",
    }));
    setSuggestions([]);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!f.label_reviewed) {
      if (!window.confirm(
        "You have not confirmed that the prescription label was reviewed. Add anyway?",
      ))
        return;
    }
    onSave(f);
  };

  return (
    <form
      data-testid="med-form"
      onSubmit={submit}
      className="mt-4 bg-white border-2 border-[#2B5339] rounded-2xl p-6 sm:p-8 space-y-5"
    >
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339]">
        {initial?.id ? "Edit medication" : "Add medication"}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block relative">
          <span className="text-xs text-[#5C6B62] font-medium">
            Generic name <span className="text-[#C26747]">*</span>
          </span>
          <input
            data-testid="mf-generic"
            required
            value={f.generic_name}
            onChange={set("generic_name")}
            className={inputCls}
            autoComplete="off"
          />
          {suggestions.length > 0 && (
            <ul className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E2DC] rounded-lg shadow-md z-10 max-h-56 overflow-y-auto">
              {suggestions.map((s) => (
                <li
                  key={s.generic}
                  data-testid={`suggest-${s.generic}`}
                  onClick={() => applySuggestion(s)}
                  className="cursor-pointer px-3 py-2 text-sm hover:bg-[#F9F8F6] border-b border-[#F0EFEA] last:border-0"
                >
                  <div className="font-medium text-[#1C2B23]">{s.display}</div>
                  <div className="text-[11px] text-[#5C6B62]">
                    {s.category}
                    {s.brands?.length ? ` · ${s.brands.join(", ")}` : ""}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </label>
        <label className="block">
          <span className="text-xs text-[#5C6B62] font-medium">Brand name</span>
          <input
            data-testid="mf-brand"
            value={f.brand_name}
            onChange={set("brand_name")}
            className={inputCls}
          />
        </label>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <label className="block">
          <span className="text-xs text-[#5C6B62] font-medium">
            Strength <span className="text-[#C26747]">*</span>
          </span>
          <input
            data-testid="mf-strength"
            required
            placeholder="e.g. 10 mg"
            value={f.strength}
            onChange={set("strength")}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="text-xs text-[#5C6B62] font-medium">Purpose</span>
          <input
            data-testid="mf-purpose"
            placeholder="e.g. Blood pressure"
            value={f.purpose}
            onChange={set("purpose")}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="text-xs text-[#5C6B62] font-medium">Form</span>
          <select
            data-testid="mf-form"
            value={f.form}
            onChange={set("form")}
            className={inputCls}
          >
            {FORMS.map((x) => (
              <option key={x} value={x}>{x}</option>
            ))}
          </select>
        </label>
      </div>

      <label className="block">
        <span className="text-xs text-[#5C6B62] font-medium">
          Prescription directions <span className="text-[#C26747]">*</span>
        </span>
        <textarea
          data-testid="mf-directions"
          required
          rows={2}
          placeholder="Copy the directions from the pharmacy label exactly."
          value={f.directions}
          onChange={set("directions")}
          className={inputCls}
        />
      </label>

      {/* Times */}
      <div>
        <div className="text-xs text-[#5C6B62] font-medium">When to take</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {TIMES.map((t) => (
            <button
              key={t.key}
              data-testid={`mf-time-${t.key}`}
              type="button"
              onClick={() => toggleTime(t.key)}
              className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                f.times.includes(t.key)
                  ? "bg-[#2B5339] border-[#2B5339] text-white"
                  : "bg-white border-[#E5E2DC] text-[#5C6B62] hover:border-[#2B5339]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs text-[#5C6B62] font-medium">Imprint on front</span>
          <input
            data-testid="mf-imprint-front"
            value={f.imprint_front}
            onChange={set("imprint_front")}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="text-xs text-[#5C6B62] font-medium">Imprint on back</span>
          <input
            data-testid="mf-imprint-back"
            value={f.imprint_back}
            onChange={set("imprint_back")}
            className={inputCls}
          />
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs text-[#5C6B62] font-medium">Manufacturer</span>
          <input
            data-testid="mf-manufacturer"
            value={f.manufacturer}
            onChange={set("manufacturer")}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="text-xs text-[#5C6B62] font-medium">Pharmacy</span>
          <input
            data-testid="mf-pharmacy"
            value={f.pharmacy}
            onChange={set("pharmacy")}
            className={inputCls}
          />
        </label>
      </div>

      <label className="block">
        <span className="text-xs text-[#5C6B62] font-medium">Do not confuse with</span>
        <input
          data-testid="mf-do-not-confuse"
          value={f.do_not_confuse}
          onChange={set("do_not_confuse")}
          placeholder="Leave blank — we suggest look-alikes automatically"
          className={inputCls}
        />
      </label>

      {/* Reference photo */}
      <div>
        <div className="text-xs text-[#5C6B62] font-medium">My reference photo (optional)</div>
        <div className="mt-2 flex items-start gap-4 flex-wrap">
          {f.photo ? (
            <div className="relative">
              <img
                data-testid="mf-photo-preview"
                src={f.photo}
                alt="Labeled prescription container reference"
                className="w-28 h-28 object-cover rounded-lg border border-[#E5E2DC]"
              />
              <button
                data-testid="mf-photo-remove"
                type="button"
                onClick={() => setF((p) => ({ ...p, photo: "" }))}
                className="absolute -top-2 -right-2 bg-[#A02E2E] text-white rounded-full p-1"
                aria-label="Remove photo"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 cursor-pointer bg-white border border-[#E5E2DC] rounded-full px-4 py-2.5 text-sm text-[#2B5339] hover:border-[#2B5339]">
              <Camera size={15} /> Photograph my labeled bottle
              <input
                data-testid="mf-photo-input"
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const dataUrl = await compressPhoto(file);
                    setF((p) => ({ ...p, photo: dataUrl }));
                  } catch {
                    /* ignore unreadable file */
                  }
                  e.target.value = "";
                }}
              />
            </label>
          )}
          <p className="text-[11px] text-[#8A6D1F] bg-[#FFF8E7] border border-[#E8DDBB] rounded-lg px-3 py-2 max-w-xs">
            <strong>A photograph is a reference aid, not proof of medication identity.</strong>{" "}
            Appearance can vary between manufacturers and refills. Stored only on this device.
          </p>
        </div>
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          data-testid="mf-label-reviewed"
          type="checkbox"
          checked={!!f.label_reviewed}
          onChange={setCheck("label_reviewed")}
          className="mt-1 w-4 h-4 accent-[#2B5339]"
        />
        <span className="text-sm text-[#1C2B23]">
          I have reviewed the prescription label against this entry.
        </span>
      </label>

      <div className="flex items-center gap-3">
        <button
          data-testid="mf-save"
          type="submit"
          className="inline-flex items-center gap-2 bg-[#2B5339] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#1C2B23]"
        >
          <CheckCircle2 size={15} /> Save medication
        </button>
        <button
          data-testid="mf-cancel"
          type="button"
          onClick={onCancel}
          className="text-sm text-[#5C6B62] hover:text-[#2B5339]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

const inputCls =
  "mt-1.5 block w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#2B5339]";
